import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { customerEndpoints } from '../../services/api/endpoints/customer.endpoint';
import { CreateCustomerDTO, UpdateCustomerDTO } from '../../types/customer';
import { logger } from '../../utils/logger';

export const useCustomerActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const createCustomer = async (data: CreateCustomerDTO) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await customerEndpoints.createCustomer(selectedOrganizationId, data);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create customer';
      setError(message);
      logger.error('Error creating customer:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCustomer = async (customerId: number, data: UpdateCustomerDTO) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await customerEndpoints.updateCustomer(
        selectedOrganizationId,
        customerId,
        data
      );
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update customer';
      setError(message);
      logger.error('Error updating customer:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCustomer = async (customerId: number) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      await customerEndpoints.deleteCustomer(selectedOrganizationId, customerId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete customer';
      setError(message);
      logger.error('Error deleting customer:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createCustomer,
    updateCustomer,
    deleteCustomer,
    isLoading,
    error,
  };
};
